const DE_TRANSFER_TAX = {
  "Baden-Württemberg": 0.05,
  Bayern: 0.035,
  Berlin: 0.06,
  Brandenburg: 0.065,
  Bremen: 0.05,
  Hamburg: 0.045,
  Hessen: 0.06,
  "Mecklenburg-Vorpommern": 0.065,
  Niedersachsen: 0.05,
  "Nordrhein-Westfalen": 0.065,
  "Rheinland-Pfalz": 0.05,
  Saarland: 0.065,
  Sachsen: 0.035,
  "Sachsen-Anhalt": 0.05,
  "Schleswig-Holstein": 0.06,
  Thüringen: 0.065,
};

const DEFAULTS = {
  title: "Objekt-Cockpit",
  country: "AT",
  state: "",
  address: "",
  purchaseDate: new Date().toISOString().slice(0, 10),
  purchasePrice: 300000,
  livingAreaSqm: 60,
  parkingSpaces: 0,
  parkingRentMonthly: 0,
  otherMonthlyRent: 0,
  coldRentPerSqmMonthly: 14,
  brokerPct: 0.0357,
  notaryPct: 0.015,
  landRegistryPct: 0.005,
  transferTaxPct: null,
  otherAcquisitionPct: 0,
  upfrontRenovation: 0,
  managementMonthly: 70,
  nonAllocableHouseMoneyMonthly: 45,
  propertyTaxMonthly: 0,
  otherOperatingMonthly: 0,
  vacancyPct: 0.04,
  maintenancePct: 0.0015,
  annualRentGrowthPct: 0.02,
  annualValueGrowthPct: 0.02,
  annualCostGrowthPct: 0.02,
  ltv: 0.8,
  rate: 0.03,
  initialRepaymentPct: 0.02,
  interestFixationYears: 10,
  collateralValue: null,
  years: 25,
  taxRate: 0.35,
  buildingSharePct: 0.75,
  baujahr: 1970,
  depreciationRatePct: null,
  projectionYears: 10,
};

function num(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function round(value, digits = 0) {
  const factor = 10 ** digits;
  return Math.round((num(value) + Number.EPSILON) * factor) / factor;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function annualAnnuityPayment(principal, annualRate, years) {
  const loan = num(principal);
  const rate = num(annualRate);
  const termMonths = Math.max(1, Math.round(num(years) * 12));
  if (loan <= 0) return 0;
  const monthlyRate = rate / 12;
  if (monthlyRate === 0) return loan / termMonths;
  return loan * monthlyRate / (1 - (1 + monthlyRate) ** -termMonths);
}

function remainingBalance(principal, annualRate, paymentMonthly, monthsElapsed) {
  const loan = num(principal);
  const rate = num(annualRate);
  const payment = num(paymentMonthly);
  const months = Math.max(0, Math.round(num(monthsElapsed)));
  if (loan <= 0) return 0;
  const monthlyRate = rate / 12;
  if (monthlyRate === 0) return Math.max(0, loan - payment * months);
  const balance = loan * (1 + monthlyRate) ** months - payment * (((1 + monthlyRate) ** months - 1) / monthlyRate);
  return Math.max(0, balance);
}

function transferTaxPctFor(country, state, override) {
  if (override != null) return num(override);
  if (String(country || "").toUpperCase() === "DE") {
    return DE_TRANSFER_TAX[state] ?? 0;
  }
  if (String(country || "").toUpperCase() === "AT") {
    return 0.035;
  }
  return 0;
}

function depreciationRateFor(baujahr, override) {
  if (override != null) return num(override);
  const year = num(baujahr);
  if (!year) return 0.02;
  if (year < 1953) return 0.025;
  if (year < 1970) return 0.02;
  return 0.02;
}

export function getObjectCockpitDefaults() {
  return { ...DEFAULTS };
}

export function calculateObjectCockpit(input = {}) {
  const cfg = { ...DEFAULTS, ...input };
  cfg.country = String(cfg.country || "AT").toUpperCase();
  cfg.state = cfg.state || "";
  cfg.transferTaxPct = transferTaxPctFor(cfg.country, cfg.state, cfg.transferTaxPct);
  cfg.depreciationRatePct = depreciationRateFor(cfg.baujahr, cfg.depreciationRatePct);
  cfg.vacancyPct = clamp(num(cfg.vacancyPct), 0, 1);
  cfg.ltv = clamp(num(cfg.ltv), 0, 1);
  cfg.taxRate = clamp(num(cfg.taxRate), 0, 1);
  cfg.buildingSharePct = clamp(num(cfg.buildingSharePct), 0, 1);

  const purchasePrice = num(cfg.purchasePrice);
  const livingAreaSqm = num(cfg.livingAreaSqm);
  const monthlyColdRent = livingAreaSqm * num(cfg.coldRentPerSqmMonthly);
  const parkingRent = num(cfg.parkingRentMonthly) * num(cfg.parkingSpaces);
  const monthlyOtherRent = num(cfg.otherMonthlyRent);
  const annualNetRent = round((monthlyColdRent + parkingRent + monthlyOtherRent) * 12);
  const annualNetRentAfterVacancy = annualNetRent * (1 - cfg.vacancyPct);

  const acquisitionCosts = round(purchasePrice * (
    num(cfg.brokerPct) +
    num(cfg.notaryPct) +
    num(cfg.landRegistryPct) +
    num(cfg.transferTaxPct) +
    num(cfg.otherAcquisitionPct)
  ));
  const downpayment = round(purchasePrice * (1 - cfg.ltv));
  const loanAmount = round(purchasePrice * cfg.ltv);
  const upfrontRenovation = round(num(cfg.upfrontRenovation));
  const equityRequired = round(downpayment + acquisitionCosts + upfrontRenovation);

  const mortgageMonthly = round(
    cfg.initialRepaymentPct != null
      ? loanAmount * (num(cfg.rate) + num(cfg.initialRepaymentPct)) / 12
      : annualAnnuityPayment(loanAmount, num(cfg.rate), num(cfg.years)),
    2,
  );
  const annualMortgage = mortgageMonthly * 12;

  const managementAnnual = num(cfg.managementMonthly) * 12;
  const nonAllocableHouseMoneyAnnual = num(cfg.nonAllocableHouseMoneyMonthly) * 12;
  const propertyTaxAnnual = num(cfg.propertyTaxMonthly) * 12;
  const otherOperatingAnnual = num(cfg.otherOperatingMonthly) * 12;
  const maintenanceAnnual = purchasePrice * num(cfg.maintenancePct);
  const operatingCostsAnnual = managementAnnual + nonAllocableHouseMoneyAnnual + propertyTaxAnnual + otherOperatingAnnual + maintenanceAnnual;
  const operatingCostsMonthly = operatingCostsAnnual / 12;

  const grossYieldPct = purchasePrice > 0 ? round((annualNetRent / purchasePrice) * 100, 2) : 0;
  const netYieldPct = purchasePrice > 0 ? round(((annualNetRent - operatingCostsAnnual) / purchasePrice) * 100, 2) : 0;
  const factor = annualNetRent > 0 ? round(purchasePrice / annualNetRent, 2) : 0;

  const interestAnnualApprox = loanAmount * num(cfg.rate);
  const depreciationAnnual = round((purchasePrice * num(cfg.buildingSharePct) + upfrontRenovation) * num(cfg.depreciationRatePct), 2);
  const taxableAnnual = annualNetRentAfterVacancy - nonAllocableHouseMoneyAnnual - interestAnnualApprox - depreciationAnnual - otherOperatingAnnual;
  const taxAnnual = Math.max(0, taxableAnnual) * num(cfg.taxRate);

  const cashflowBeforeTaxAnnual = annualNetRentAfterVacancy - operatingCostsAnnual - annualMortgage;
  const cashflowAfterTaxAnnual = cashflowBeforeTaxAnnual - taxAnnual;
  const cashflowBeforeTaxMonthly = cashflowBeforeTaxAnnual / 12;
  const cashflowAfterTaxMonthly = cashflowAfterTaxAnnual / 12;
  const cashOnCashPct = equityRequired > 0 ? round((cashflowAfterTaxAnnual / equityRequired) * 100, 2) : 0;

  const monthlyPayment = annualMortgage / 12;
  const debtFreeYear = (() => {
    if (loanAmount <= 0 || monthlyPayment <= 0) return null;
    for (let month = 0; month <= num(cfg.years) * 12; month += 1) {
      if (remainingBalance(loanAmount, num(cfg.rate), monthlyPayment, month) <= 1) {
        return Math.ceil(month / 12);
      }
    }
    return null;
  })();

  const timeline = [];
  for (let yearOffset = 0; yearOffset <= num(cfg.projectionYears); yearOffset += 1) {
    const rentFactor = (1 + num(cfg.annualRentGrowthPct)) ** yearOffset;
    const valueFactor = (1 + num(cfg.annualValueGrowthPct)) ** yearOffset;
    const costFactor = (1 + num(cfg.annualCostGrowthPct)) ** yearOffset;
    const yearlyNetRent = annualNetRent * rentFactor;
    const yearlyNetRentAfterVacancy = yearlyNetRent * (1 - cfg.vacancyPct);
    const yearlyOperating = operatingCostsAnnual * costFactor;
    const propertyValue = purchasePrice * valueFactor;
    const balance = remainingBalance(loanAmount, num(cfg.rate), monthlyPayment, yearOffset * 12);
    const interestApprox = balance * num(cfg.rate);
    const taxable = yearlyNetRentAfterVacancy - (nonAllocableHouseMoneyAnnual * costFactor) - interestApprox - depreciationAnnual - (otherOperatingAnnual * costFactor);
    const annualTax = Math.max(0, taxable) * num(cfg.taxRate);
    const cashBeforeTax = yearlyNetRentAfterVacancy - yearlyOperating - annualMortgage;
    const cashAfterTax = cashBeforeTax - annualTax;

    timeline.push({
      yearOffset,
      year: new Date().getFullYear() + yearOffset,
      annualNetRent: round(yearlyNetRent),
      annualNetRentAfterVacancy: round(yearlyNetRentAfterVacancy),
      annualOperatingCosts: round(yearlyOperating),
      propertyValue: round(propertyValue),
      loanBalance: round(balance),
      equity: round(propertyValue - balance),
      cashflowBeforeTaxMonthly: round(cashBeforeTax / 12, 2),
      cashflowAfterTaxMonthly: round(cashAfterTax / 12, 2),
      taxMonthly: round(annualTax / 12, 2),
    });
  }

  return {
    inputs: cfg,
    annualNetRent: round(annualNetRent),
    annualNetRentAfterVacancy: round(annualNetRentAfterVacancy),
    grossYieldPct,
    netYieldPct,
    factor,
    acquisitionCosts,
    downpayment,
    upfrontRenovation,
    equityRequired,
    loanAmount,
    mortgageMonthly,
    annualMortgage: round(annualMortgage),
    operatingCostsAnnual: round(operatingCostsAnnual),
    operatingCostsMonthly: round(operatingCostsMonthly, 2),
    maintenanceAnnual: round(maintenanceAnnual),
    depreciationAnnual,
    taxAnnual: round(taxAnnual, 2),
    cashflowBeforeTaxMonthly: round(cashflowBeforeTaxMonthly, 2),
    cashflowAfterTaxMonthly: round(cashflowAfterTaxMonthly, 2),
    cashflowBeforeTaxAnnual: round(cashflowBeforeTaxAnnual, 2),
    cashflowAfterTaxAnnual: round(cashflowAfterTaxAnnual, 2),
    cashOnCashPct,
    debtFreeYear,
    monthlyColdRent: round(monthlyColdRent),
    monthlyWarmRent: round(monthlyColdRent + num(cfg.otherMonthlyRent) + parkingRent),
    timeline,
  };
}
